import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ManageConcepts.css';

// ─── Sound Engine ─────────────────────────────────────────────────────────────
const useSoundEngine = () => {
  const ctx = useRef(null);
  const getCtx = () => {
    if (!ctx.current) ctx.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.current.state === 'suspended') ctx.current.resume();
    return ctx.current;
  };
  const playTone = useCallback((freq, type = 'sine', duration = 0.1, vol = 0.08, delay = 0) => {
    try {
      const ac = getCtx();
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.connect(gain); gain.connect(ac.destination);
      osc.type = type; osc.frequency.setValueAtTime(freq, ac.currentTime + delay);
      gain.gain.setValueAtTime(vol, ac.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + delay + duration);
      osc.start(ac.currentTime + delay); osc.stop(ac.currentTime + delay + duration);
    } catch (e) {}
  }, []);

  return {
    hover:    () => playTone(660, 'sine', 0.05, 0.04),
    click:    () => { playTone(440, 'sine', 0.07, 0.08); playTone(550, 'sine', 0.06, 0.06, 0.04); },
    add:      () => { [330, 440, 550].forEach((f, i) => playTone(f, 'sine', 0.1, 0.1, i * 0.06)); },
    remove:   () => { [440, 330, 220].forEach((f, i) => playTone(f, 'sine', 0.08, 0.08, i * 0.05)); },
    check:    () => { playTone(550, 'sine', 0.07, 0.08); playTone(660, 'sine', 0.05, 0.06, 0.06); },
    uncheck:  () => playTone(330, 'sine', 0.08, 0.07),
    expand:   () => { [330, 440].forEach((f, i) => playTone(f, 'sine', 0.08, 0.07, i * 0.05)); },
    collapse: () => { [440, 330].forEach((f, i) => playTone(f, 'sine', 0.08, 0.07, i * 0.05)); },
    save:     () => { [330, 440, 550, 660].forEach((f, i) => playTone(f, 'sine', 0.1, 0.12, i * 0.05)); },
    warn:     () => { playTone(220, 'sine', 0.08, 0.15); playTone(220, 'sine', 0.08, 0.15, 0.15); },
    back:     () => { playTone(440, 'sine', 0.05, 0.08); playTone(330, 'sine', 0.07, 0.08, 0.06); },
    modal:    () => { [440, 550, 660].forEach((f, i) => playTone(f, 'sine', 0.1, 0.1, i * 0.06)); },
    toast:    () => playTone(660, 'sine', 0.12, 0.1),
  };
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ManageConcepts = () => {
  const navigate = useNavigate();
  const sfx = useSoundEngine();

  const [selectedConcepts, setSelectedConcepts] = useState([]);
  const [unselectedConcepts, setUnselectedConcepts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedSubconceptsCount, setSelectedSubconceptsCount] = useState(0);
  const [masteredCount, setMasteredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedSubconcepts, setExpandedSubconcepts] = useState({});
  const [animatedCards, setAnimatedCards] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedConceptForAdd, setSelectedConceptForAdd] = useState(null);
  const [tempSubconceptSelections, setTempSubconceptSelections] = useState({});
  const [addedConcepts, setAddedConcepts] = useState(new Set());
  const [removedConcepts, setRemovedConcepts] = useState(new Set());
  const [subconceptChanges, setSubconceptChanges] = useState({});

  useEffect(() => { loadData(); }, []);

  const showToast = useCallback((message, type = 'success') => {
    sfx.toast();
    setToast({ message, type });
    setTimeout(() => setToast(null), 2800);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/parent-manage-concepts');
      const data = await response.json();
      if (data.success) {
        setSelectedConcepts(data.selected_concepts || []);
        setUnselectedConcepts(data.unselected_concepts || []);
        setTotalCount(data.total_count || 0);
        setSelectedCount(data.selected_count || 0);
        updateCounts(data.selected_concepts || []);
      }
    } catch (error) {
      showToast('Error loading concepts', 'error');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const updateCounts = (concepts) => {
    let sub = 0, mas = 0;
    concepts.forEach(c => {
      if (c.subconcepts) {
        sub += c.subconcepts.filter(s => s.is_selected).length;
        mas += c.subconcepts.filter(s => s.mastery_level >= 80).length;
      }
    });
    setSelectedSubconceptsCount(sub);
    setMasteredCount(mas);
  };

  const animateCard = (conceptId) => {
    setAnimatedCards(prev => new Set([...prev, conceptId]));
    setTimeout(() => setAnimatedCards(prev => { const s = new Set(prev); s.delete(conceptId); return s; }), 500);
  };

  const openAddModal = (concept) => {
    sfx.modal();
    const init = {};
    concept.subconcepts.forEach(s => { init[s.id] = false; });
    setTempSubconceptSelections(init);
    setSelectedConceptForAdd(concept);
    setShowAddModal(true);
  };

  const toggleModalSubconcept = (id) => {
    sfx.check();
    setTempSubconceptSelections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllModalSubconcepts = () => {
    const allSel = Object.values(tempSubconceptSelections).every(v => v);
    sfx[allSel ? 'uncheck' : 'check']();
    const next = {};
    Object.keys(tempSubconceptSelections).forEach(id => { next[id] = !allSel; });
    setTempSubconceptSelections(next);
  };

  const confirmAddConcept = () => {
    const concept = selectedConceptForAdd;
    const hasAny = Object.values(tempSubconceptSelections).some(v => v);
    if (!hasAny) { sfx.warn(); showToast('Please select at least one subconcept.', 'warning'); return; }

    const updatedSubs = concept.subconcepts.map(s => ({ ...s, is_selected: tempSubconceptSelections[s.id] || false }));
    const selCount = updatedSubs.filter(s => s.is_selected).length;
    const conceptToAdd = { ...concept, subconcepts: updatedSubs, selected_subconcepts: selCount };

    setSelectedConcepts(prev => [...prev, conceptToAdd]);
    setUnselectedConcepts(prev => prev.filter(c => c.concept.id !== concept.concept.id));
    setAddedConcepts(prev => new Set([...prev, concept.concept.id]));
    setRemovedConcepts(prev => { const s = new Set(prev); s.delete(concept.concept.id); return s; });

    const selIds = updatedSubs.filter(s => s.is_selected).map(s => s.id);
    if (selIds.length > 0) {
      setSubconceptChanges(prev => ({ ...prev, [concept.concept.id]: { selected: selIds, unselected: updatedSubs.filter(s => !s.is_selected).map(s => s.id) } }));
    }

    setSelectedCount(prev => prev + 1);
    updateCounts([...selectedConcepts, conceptToAdd]);
    animateCard(concept.concept.id);
    sfx.add();
    showToast(`"${concept.concept.name}" added with ${selCount} subconcepts`, 'success');
    setShowAddModal(false); setSelectedConceptForAdd(null); setTempSubconceptSelections({});
  };

  const updateSubconceptSelection = (conceptId, subconceptId, isSelected) => {
    isSelected ? sfx.check() : sfx.uncheck();
    setSelectedConcepts(prev => prev.map(concept => {
      if (concept.concept.id !== conceptId) return concept;
      const updated = concept.subconcepts.map(sc => sc.id === subconceptId ? { ...sc, is_selected: isSelected } : sc);
      return { ...concept, subconcepts: updated, selected_subconcepts: updated.filter(s => s.is_selected).length };
    }));
    setSubconceptChanges(prev => {
      const ch = { ...prev };
      if (!ch[conceptId]) ch[conceptId] = { selected: [], unselected: [] };
      if (isSelected) {
        if (!ch[conceptId].selected.includes(subconceptId)) ch[conceptId].selected.push(subconceptId);
        ch[conceptId].unselected = ch[conceptId].unselected.filter(id => id !== subconceptId);
      } else {
        if (!ch[conceptId].unselected.includes(subconceptId)) ch[conceptId].unselected.push(subconceptId);
        ch[conceptId].selected = ch[conceptId].selected.filter(id => id !== subconceptId);
      }
      return ch;
    });
    updateCounts(selectedConcepts);
  };

  const toggleAllSubconcepts = (conceptId) => {
    const concept = selectedConcepts.find(c => c.concept.id === conceptId);
    if (concept?.subconcepts) {
      const allSel = concept.subconcepts.every(s => s.is_selected);
      sfx[allSel ? 'uncheck' : 'check']();
      concept.subconcepts.forEach(sub => updateSubconceptSelection(conceptId, sub.id, !allSel));
    }
  };

  const removeConcept = (concept) => {
    sfx.remove();
    setRemovedConcepts(prev => new Set([...prev, concept.concept.id]));
    setAddedConcepts(prev => { const s = new Set(prev); s.delete(concept.concept.id); return s; });
    setUnselectedConcepts(prev => [...prev, concept]);
    setSelectedConcepts(prev => prev.filter(c => c.concept.id !== concept.concept.id));
    setSelectedCount(prev => prev - 1);
    animateCard(concept.concept.id);
    showToast(`"${concept.concept.name}" removed`, 'info');
    setSubconceptChanges(prev => { const ch = { ...prev }; delete ch[concept.concept.id]; return ch; });
    updateCounts(selectedConcepts.filter(c => c.concept.id !== concept.concept.id));
  };

  const saveChanges = async () => {
    const hasMain = addedConcepts.size > 0 || removedConcepts.size > 0;
    const hasSub = Object.keys(subconceptChanges).length > 0;
    if (!hasMain && !hasSub) { sfx.warn(); showToast('No changes to save.', 'info'); return; }
    try {
      sfx.save();
      showToast('Saving changes...', 'loading');
      const response = await fetch('/api/parent-save-concept-changes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ add_concepts: Array.from(addedConcepts), remove_concepts: Array.from(removedConcepts), subconcept_changes: subconceptChanges })
      });
      const data = await response.json();
      if (data.success) {
        showToast('Changes saved successfully!', 'success');
        setTimeout(() => { window.location.href = '/parent-dashboard'; }, 1500);
      } else {
        showToast('Error: ' + data.message, 'error');
      }
    } catch {
      showToast('Error saving changes', 'error');
    }
  };

  const toggleSubconcepts = (conceptId) => {
    const isExpanding = !expandedSubconcepts[conceptId];
    sfx[isExpanding ? 'expand' : 'collapse']();
    setExpandedSubconcepts(prev => ({ ...prev, [conceptId]: !prev[conceptId] }));
  };

  const showMessage = (msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); };

  const areAllSelected = (concept) => concept.subconcepts?.length > 0 && concept.subconcepts.every(s => s.is_selected);
  const areSomeSelected = (concept) => concept.subconcepts?.some(s => s.is_selected) && !concept.subconcepts.every(s => s.is_selected);

  const totalSubconcepts = selectedConcepts.reduce((sum, c) => sum + (c.subconcepts?.length || 0), 0);
  const overallProgress = totalSubconcepts > 0 ? (selectedSubconceptsCount / totalSubconcepts) * 100 : 0;
  const pendingChanges = addedConcepts.size + removedConcepts.size + Object.keys(subconceptChanges).length;

  const getMasteryInfo = (m) => {
    if (m >= 80) return { label: 'Mastered', color: '#059669', bg: '#D1FAE5' };
    if (m >= 50) return { label: 'Proficient', color: '#2563EB', bg: '#DBEAFE' };
    if (m > 0)  return { label: 'Learning', color: '#D97706', bg: '#FEF3C7' };
    return { label: 'New', color: '#6B7280', bg: '#F3F4F6' };
  };

  // ── Loading Screen ──
  if (loading) {
    return (
      <div className="mc-loading-screen">
        <div className="mc-loading-card">
          <div className="mc-loading-spinner" />
          <p className="mc-loading-text">Loading your child's learning plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-root">

      {/* Toast */}
      {toast && (
        <div className={`mc-toast mc-toast--${toast.type}`}>
          <span className="mc-toast-dot" />
          <span>{toast.message}</span>
        </div>
      )}

      {/* Success banner */}
      {successMessage && <div className="mc-banner">{successMessage}</div>}

      <div className="mc-layout">

        {/* ── Sidebar ── */}
        <aside className="mc-sidebar">
          <div className="mc-sidebar-brand">
            <div className="mc-brand-icon">📚</div>
            <div>
              <div className="mc-brand-title">Learning Manager</div>
              <div className="mc-brand-sub">Manage your child's topics</div>
            </div>
          </div>

          <div className="mc-sidebar-divider" />

          {/* Stats */}
          <div className="mc-stats-group">
            <p className="mc-stats-label">Overview</p>
            {[
              { icon: '📋', label: 'Total Topics', value: totalCount },
              { icon: '✅', label: 'Active Topics', value: selectedCount },
              { icon: '📌', label: 'Subtopics Active', value: selectedSubconceptsCount },
              { icon: '🏅', label: 'Mastered', value: masteredCount },
            ].map((s, i) => (
              <div className="mc-stat-row" key={i}>
                <span className="mc-stat-icon">{s.icon}</span>
                <span className="mc-stat-label">{s.label}</span>
                <span className="mc-stat-value">{s.value}</span>
              </div>
            ))}
          </div>

          <div className="mc-sidebar-divider" />

          {/* Progress */}
          <div className="mc-progress-group">
            <div className="mc-progress-header">
              <span className="mc-stats-label">Progress</span>
              <span className="mc-progress-pct">{Math.round(overallProgress)}%</span>
            </div>
            <div className="mc-progress-bar">
              <div className="mc-progress-fill" style={{ width: `${overallProgress}%` }} />
            </div>
            <p className="mc-progress-sub">{selectedSubconceptsCount} of {totalSubconcepts} subtopics selected</p>
          </div>

          <div className="mc-sidebar-spacer" />

          {/* Actions */}
          <div className="mc-sidebar-actions">
            <button
              className="mc-btn-secondary"
              onMouseEnter={() => sfx.hover()}
              onClick={() => { sfx.back(); navigate('/parent-dashboard'); }}
            >
              ← Back to Dashboard
            </button>
            <button
              className="mc-btn-primary"
              onMouseEnter={() => sfx.hover()}
              onClick={saveChanges}
            >
              Save Changes
              {pendingChanges > 0 && <span className="mc-changes-pill">{pendingChanges}</span>}
            </button>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="mc-main">

          <div className="mc-main-header">
            <div>
              <h1 className="mc-page-title">Manage Learning Topics</h1>
              <p className="mc-page-desc">Select topics and subtopics your child should practice. Changes apply to their daily question set.</p>
            </div>
            <button
              className="mc-expand-btn"
              onMouseEnter={() => sfx.hover()}
              onClick={() => {
                sfx.click();
                const allExp = selectedConcepts.every(c => expandedSubconcepts[c.concept.id]);
                const next = {};
                if (!allExp) selectedConcepts.forEach(c => { next[c.concept.id] = true; });
                setExpandedSubconcepts(next);
              }}
            >
              {selectedConcepts.every(c => expandedSubconcepts[c.concept.id]) ? '↑ Collapse All' : '↓ Expand All'}
            </button>
          </div>

          {/* ═══ ACTIVE TOPICS ═══ */}
          <section className="mc-section">
            <div className="mc-section-header">
              <div className="mc-section-title-row">
                <span className="mc-section-dot mc-dot-green" />
                <h2 className="mc-section-title">Active Topics</h2>
                <span className="mc-count-badge mc-count-green">{selectedConcepts.length}</span>
              </div>
              <p className="mc-section-desc">These topics are currently included in your child's learning plan.</p>
            </div>

            {selectedConcepts.length === 0 ? (
              <div className="mc-empty-state">
                <div className="mc-empty-icon">📭</div>
                <p className="mc-empty-title">No active topics</p>
                <p className="mc-empty-desc">Add topics from the available list below to get started.</p>
              </div>
            ) : (
              <div className="mc-cards-grid">
                {selectedConcepts.map((data) => {
                  const pct = data.total_subconcepts > 0 ? (data.selected_subconcepts / data.total_subconcepts) * 100 : 0;
                  const isExpanded = expandedSubconcepts[data.concept.id];
                  const isAnimated = animatedCards.has(data.concept.id);
                  return (
                    <div key={data.concept.id} className={`mc-card mc-card--active ${isAnimated ? 'mc-card--pop' : ''}`}>
                      <div className="mc-card-top">
                        <div className="mc-card-info">
                          <h3 className="mc-card-title">{data.concept.name}</h3>
                          <div className="mc-card-tags">
                            <span className="mc-tag">Grade {data.concept.grade}</span>
                            <span className="mc-tag">{data.concept.stream}</span>
                            <span className="mc-tag mc-tag--blue">{data.selected_subconcepts}/{data.total_subconcepts} subtopics</span>
                          </div>
                        </div>
                        <button
                          className="mc-action-btn mc-action-btn--remove"
                          onMouseEnter={() => sfx.hover()}
                          onClick={() => removeConcept(data)}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="mc-card-progress">
                        <div className="mc-card-prog-bar">
                          <div className="mc-card-prog-fill mc-fill-green" style={{ width: `${pct}%` }} />
                        </div>
                      </div>

                      <button
                        className="mc-subtopic-toggle"
                        onMouseEnter={() => sfx.hover()}
                        onClick={() => toggleSubconcepts(data.concept.id)}
                      >
                        <span>{isExpanded ? '▲ Hide' : '▼ Show'} subtopics</span>
                        <span className="mc-active-count">{data.subconcepts?.filter(s => s.is_selected).length || 0} active</span>
                      </button>

                      {isExpanded && data.subconcepts && (
                        <div className="mc-subtopics">
                          <div
                            className="mc-select-all"
                            onClick={() => toggleAllSubconcepts(data.concept.id)}
                          >
                            <input
                              type="checkbox"
                              checked={areAllSelected(data)}
                              ref={input => { if (input) input.indeterminate = areSomeSelected(data); }}
                              readOnly
                            />
                            <span>Select all subtopics</span>
                          </div>

                          {data.subconcepts.map(sub => {
                            const mastery = getMasteryInfo(sub.mastery_level);
                            return (
                              <div key={sub.id} className={`mc-subtopic-row ${sub.is_selected ? 'mc-subtopic-row--active' : ''}`}>
                                <div className="mc-subtopic-left">
                                  <input
                                    type="checkbox"
                                    checked={sub.is_selected}
                                    onChange={(e) => updateSubconceptSelection(data.concept.id, sub.id, e.target.checked)}
                                  />
                                  <span className="mc-subtopic-name">{sub.name}</span>
                                </div>
                                <div className="mc-subtopic-right">
                                  <span className="mc-mastery-badge" style={{ color: mastery.color, background: mastery.bg }}>
                                    {sub.total_attempts > 0 ? `${Math.round(sub.mastery_level)}%` : mastery.label}
                                  </span>
                                  {sub.total_attempts > 0 && (
                                    <span className="mc-attempts">{sub.correct_attempts}/{sub.total_attempts}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ═══ AVAILABLE TOPICS ═══ */}
          <section className="mc-section">
            <div className="mc-section-header">
              <div className="mc-section-title-row">
                <span className="mc-section-dot mc-dot-amber" />
                <h2 className="mc-section-title">Available Topics</h2>
                <span className="mc-count-badge mc-count-amber">{unselectedConcepts.length}</span>
              </div>
              <p className="mc-section-desc">Add these topics to include them in your child's practice sessions.</p>
            </div>

            {unselectedConcepts.length === 0 ? (
              <div className="mc-empty-state">
                <div className="mc-empty-icon">🎉</div>
                <p className="mc-empty-title">All topics are active!</p>
                <p className="mc-empty-desc">Your child is covering all available topics.</p>
              </div>
            ) : (
              <div className="mc-cards-grid">
                {unselectedConcepts.map((data) => (
                  <div key={data.concept.id} className={`mc-card mc-card--available ${animatedCards.has(data.concept.id) ? 'mc-card--pop' : ''}`}>
                    <div className="mc-card-top">
                      <div className="mc-card-info">
                        <h3 className="mc-card-title">{data.concept.name}</h3>
                        <div className="mc-card-tags">
                          <span className="mc-tag">Grade {data.concept.grade}</span>
                          <span className="mc-tag">{data.concept.stream}</span>
                          <span className="mc-tag mc-tag--amber">{data.total_subconcepts} subtopics</span>
                        </div>
                      </div>
                      <button
                        className="mc-action-btn mc-action-btn--add"
                        onMouseEnter={() => sfx.hover()}
                        onClick={() => openAddModal(data)}
                      >
                        + Add
                      </button>
                    </div>
                    <p className="mc-card-desc">
                      {data.concept.description || 'Click Add to choose which subtopics to include in the learning plan.'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      {/* ── Add Subconcept Modal ── */}
      {showAddModal && selectedConceptForAdd && (
        <div className="mc-overlay" onClick={() => { sfx.back(); setShowAddModal(false); }}>
          <div className="mc-modal" onClick={e => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div>
                <h3 className="mc-modal-title">Select Subtopics</h3>
                <p className="mc-modal-subtitle">"{selectedConceptForAdd.concept.name}"</p>
              </div>
              <button
                className="mc-modal-close"
                onMouseEnter={() => sfx.hover()}
                onClick={() => { sfx.back(); setShowAddModal(false); }}
              >✕</button>
            </div>

            <div className="mc-modal-info-bar">
              <span>Choose subtopics to include in your child's learning path</span>
              <span className="mc-modal-count">
                {Object.values(tempSubconceptSelections).filter(v => v).length} / {selectedConceptForAdd.subconcepts.length} selected
              </span>
            </div>

            <div className="mc-modal-body">
              <div className="mc-modal-select-all" onClick={toggleAllModalSubconcepts}>
                <input
                  type="checkbox"
                  checked={Object.values(tempSubconceptSelections).every(v => v)}
                  readOnly
                />
                <span>Select all</span>
              </div>

              <div className="mc-modal-list">
                {selectedConceptForAdd.subconcepts.map((sub) => (
                  <div
                    key={sub.id}
                    className={`mc-modal-item ${tempSubconceptSelections[sub.id] ? 'mc-modal-item--selected' : ''}`}
                    onClick={() => toggleModalSubconcept(sub.id)}
                  >
                    <input
                      type="checkbox"
                      checked={tempSubconceptSelections[sub.id] || false}
                      onChange={() => toggleModalSubconcept(sub.id)}
                      onClick={e => e.stopPropagation()}
                    />
                    <span>{sub.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mc-modal-footer">
              <button
                className="mc-btn-secondary"
                onMouseEnter={() => sfx.hover()}
                onClick={() => { sfx.back(); setShowAddModal(false); }}
              >Cancel</button>
              <button
                className="mc-btn-primary"
                onMouseEnter={() => sfx.hover()}
                onClick={confirmAddConcept}
              >Add to Learning Plan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageConcepts;